import React, { useMemo, useState } from 'react';
import { DataForm } from './forms';
// Import shared formatting utilities
import { 
    formatFieldValue, 
    formatDateValue, 
    formatTimeValue, 
    formatNumberValue, 
    formatCurrencyValue, 
    applyTextFormatting,
    formatNumber
} from '../utils/formatters';

// Shared component for rendering DDR sections - used by both screen and print
const ReportRenderer = ({ 
  layoutConfig = [],
  sectionData = {},
  well = {},
  reportDate = '',
  reportData = {},
  currentReport = null,
  isPrint = false,
  fontScale = 1,
  // Screen-only props for editing functionality
  editingSections = {},
  editingData = {},
  collapsedSections = {},
  role = 'read-only',
  toggleSection = () => {},
  toggleEditSection = () => {},
  addTableRow = () => {},
  removeTableRow = () => {},
  sectionRefs = { current: {} },
  // Add Data functionality
  handleAddDataToEmptySection = () => {},
  // Copy Data functionality
  copiedSections = new Set(),
  setCopiedSections = () => {},
  isCopyingData = false,
  setIsCopyingData = () => {},
  setToast = () => {},
  formatDate = () => {},
  apiFetch = () => {},
  setEditingData = () => {},
  setEditingSections = () => {},
  handleFieldChange = () => {},
  handleTableFieldChange = () => {},
  saveSectionData = () => {},
  // Enhanced form rendering functions
  renderFieldInput = null,
  renderTableCellInput = null,
  globalSettings = {},
  // DataForm props
  showDataForm = false,
  setShowDataForm = () => {},
  dataFormConfig = null,
  setDataFormConfig = () => {},
  handleDataFormSave = () => {},
  handleDataFormCancel = () => {},
  getSectionDataProp = () => {}
}) => {
  
  // Formatting functions are now imported from ../utils/formatters

  const getSectionData = (sectionConfig) => {
    const data = sectionData[sectionConfig.id];
    if (!data) return {};
    
    // Handle different data types
    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : {};
    }
    
    return data;
  };

  // CollapsibleSection component for screen mode (exact same as DailyDrillingReport.js)
  const CollapsibleSection = ({ title, sectionKey, children, isEmpty = false, onSave, onCancel, onAddData, onCopyData }) => {
    const isCollapsed = collapsedSections[sectionKey];
    const isEditing = editingSections[sectionKey];
    const isCopied = copiedSections.has(sectionKey);

    const handleSectionInteraction = (e, interactionType) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (interactionType === 'toggle') {
        toggleSection(sectionKey);
      } else if (interactionType === 'edit') {
        toggleEditSection(sectionKey);
      }
    };

    const handleHeaderClick = (e) => {
      // Only toggle if clicking on the header itself, not on buttons
      if (
        e.target === e.currentTarget ||
        e.target.classList.contains('section-title') ||
        e.target.classList.contains('collapse-icon') ||
        e.target.closest('.section-title')
      ) {
        handleSectionInteraction(e, 'toggle');
      }
    };

    const handleContentDoubleClick = (e) => {
      // Double-click on content to edit section (if not empty and user has permission)
      if (!isEmpty && (role === 'admin' || role === 'read-write') && !isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handleSectionInteraction(e, 'edit');
      }
    };

    const handleContentRightClick = (e) => {
      // Optionally: do nothing, or call a context menu handler if passed as prop
      e.preventDefault();
      e.stopPropagation();
      // If you want to support context menu, call a prop here
      // if (onContextMenu) onContextMenu(e, sectionKey);
    };

    return (
      <div className={`collapsible-section ${isCopied ? 'copied-section' : ''}`}>
        <div className="section-header" onClick={handleHeaderClick}>
          <div className="section-title" onClick={(e) => handleSectionInteraction(e, 'toggle')}>
            <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
              {isCollapsed ? '▶' : '▼'}
            </span>
            {title}
            {isCopied && <span className="copied-indicator">📋 Copied</span>}
          </div>
          <div className="section-actions">
            {isEditing && (
              <div className="editing-buttons" onClick={(e) => e.stopPropagation()}>
                <button 
                  className="edit-full-data-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('🔧 Edit Full Data button clicked in ReportRenderer:', sectionKey);
                    
                    // Find the section config by sectionKey
                    const sectionConfig = layoutConfig?.sections?.find(s => s.id === sectionKey);
                    
                    if (!sectionConfig?.tableName) {
                      setToast('❌ No table configuration found for this section');
                      setTimeout(() => setToast(''), 3000);
                      return;
                    }
                    
                    // Get current section data
                    const currentData = getSectionDataProp(sectionConfig);
                    const dataToEdit = sectionConfig.presentationType === 'table' 
                      ? (Array.isArray(currentData) && currentData.length > 0 ? currentData[0] : {})
                      : currentData;
                    
                    console.log('🔧 Opening DataForm for section:', {
                      sectionKey,
                      tableName: sectionConfig.tableName,
                      sectionName: sectionConfig.name,
                      dataToEdit
                    });
                    
                    // Set up DataForm configuration
                    setDataFormConfig({
                      tableName: sectionConfig.tableName,
                      data: dataToEdit,
                      sectionConfig: sectionConfig
                    });
                    setShowDataForm(true);
                  }}
                  title="Open comprehensive data form for this section"
                  disabled={false}
                >
                  🔧 Edit Full Data
                </button>
                <button 
                  className="save-section-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSave) onSave();
                  }}
                >
                  💾 Save
                </button>
                <button 
                  className="cancel-section-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCancel) onCancel();
                  }}
                >
                  ❌ Cancel
                </button>
              </div>
            )}

          </div>
        </div>
        <div className={`section-content ${isCollapsed ? 'collapsed' : ''}`}
             onDoubleClick={handleContentDoubleClick} 
             onContextMenu={handleContentRightClick}>
          {isEditing ? (
            children
          ) : isEmpty && (role === 'admin' || role === 'read-write') ? (
            <div className="empty-section-content">
              <div className="empty-section-message">
                <p>No data available</p>
              </div>
              <div className="empty-section-actions">
                {onAddData && (
                  <button 
                    className="add-data-btn-content"
                    onClick={onAddData}
                    title="Add new data to this section"
                  >
                    ➕ Add Data
                  </button>
                )}
                {onCopyData && (
                  <button 
                    className="copy-data-btn-content"
                    onClick={onCopyData}
                    disabled={isCopyingData}
                    title="Copy data from latest report"
                  >
                    {isCopyingData ? '⏳ Copying...' : '📋 Copy Data'}
                  </button>
                )}
              </div>
            </div>
          ) : isEmpty ? (
            <div className="empty-section-content">
              <div className="empty-section-message">
                <p>No data available</p>
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    );
  };

  // Render dynamic section (same logic as DailyDrillingReport.js but with editing support)
  const renderDynamicSection = (sectionConfig) => {
    const data = getSectionData(sectionConfig);
    const isEditing = editingSections[sectionConfig.id];
    const editData = editingData[sectionConfig.id] || {};

    if (sectionConfig.presentationType === 'table') {
      return renderTableSection(sectionConfig, data, isEditing, editData);
    } else {
      return renderLabelValueSection(sectionConfig, data, isEditing, editData);
    }
  };

  // Render table section with editing support
  const renderTableSection = (sectionConfig, data, isEditing, editData) => {
    const tableData = Array.isArray(sectionData[sectionConfig.id]) ? sectionData[sectionConfig.id] : [];
    const editableTableData = isEditing ? (editData.tableRows || [...tableData]) : tableData;
    const tableClassName = `table-section-${sectionConfig.id}`;

    const handleSave = async () => {
      try {
        await saveSectionData(sectionConfig, editData);
        toggleEditSection(sectionConfig.id);
      } catch (err) {
        console.error('Failed to save section:', err);
      }
    };

    const handleCancel = () => {
      // Clear editing data and exit edit mode
      setEditingData(prev => {
        const newData = { ...prev };
        delete newData[sectionConfig.id];
        return newData;
      });
      setEditingSections(prev => ({ ...prev, [sectionConfig.id]: false }));
      setCopiedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionConfig.id);
        return newSet;
      });
      setToast && setToast(`🚫 Changes cancelled for ${sectionConfig.name}`);
      setTimeout(() => setToast && setToast(''), 3000);
    };

    return (
      <div key={sectionConfig.id} ref={sectionRefs.current[sectionConfig.id]}>
        <CollapsibleSection 
          title={sectionConfig.name} 
          sectionKey={sectionConfig.id}
          isEmpty={tableData.length === 0}
          onSave={handleSave}
          onCancel={handleCancel}
          onAddData={() => handleAddDataToEmptySection(sectionConfig)}
          onCopyData={() => handleCopyDataFromLatest(sectionConfig)}
        >
          <div className={`table-container ${tableClassName}`}>
            {/* Add scoped custom CSS if provided */}
            {sectionConfig.customTableCSS && (
              <style>
                {sectionConfig.customTableCSS
                  .replace(/\btable\b/g, `.${tableClassName} table`)
                  .replace(/\bth\b/g, `.${tableClassName} th`)
                  .replace(/\btr\b/g, `.${tableClassName} tr`)
                  .replace(/\btd\b/g, `.${tableClassName} td`)
                  .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                    const importantRules = rules.replace(
                      /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                      '$1$2 !important'
                    );
                    return `${selector}{${importantRules}}`;
                  })
                }
              </style>
            )}
            
            <table className="activity-table" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>
              <thead>
                <tr>
                  {sectionConfig.fields
                    .filter(field => field.enabled)
                    .map(field => (
                      <th key={field.id} style={{ width: field.width !== 'auto' ? field.width : undefined }}>
                        {field.name}
                      </th>
                    ))}
                  {isEditing && !isPrint && <th style={{ width: '60px' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {editableTableData.length > 0 ? (
                  editableTableData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {sectionConfig.fields
                        .filter(field => field.enabled)
                        .map(field => (
                          <td key={field.id}>
                            {isEditing && !isPrint ? (
                              renderTableCellInputToUse(
                                field,
                                sectionConfig.id,
                                row[field.dbColumn] || '',
                                rowIndex,
                                updateTableCellData
                              )
                            ) : (
                              formatFieldValue(row[field.dbColumn], field, globalSettings)
                            )}
                          </td>
                        ))}
                      {isEditing && !isPrint && (
                        <td>
                          <button 
                            className="remove-table-row-btn"
                            onClick={() => removeTableRow(sectionConfig.id, rowIndex)}
                            title="Remove row"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={sectionConfig.fields.filter(f => f.enabled).length + (isEditing && !isPrint ? 1 : 0)}>
                      {isEditing && !isPrint ? 'Click "Add Row" to add data' : 'No data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {isEditing && !isPrint && (
              <div className="table-editing-controls-bottom">
                <button 
                  className="add-table-row-btn"
                  onClick={async () => await addTableRow(sectionConfig.id, sectionConfig.fields)}
                >
                  ➕ Add Row
                </button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      </div>
    );
  };

  // Render label-value section with groups and editing support
  const renderLabelValueSection = (sectionConfig, data, isEditing, editData) => {
    const hasData = Object.keys(data).length > 0 || isEditing;
    const enabledFields = sectionConfig.fields.filter(field => field.enabled);
    
    // Group fields by their group property
    const fieldGroups = enabledFields.reduce((groups, field) => {
      const groupId = field.group || 1;
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(field);
      return groups;
    }, {});

    // Get group settings with fallback
    const groupSettings = sectionConfig.groupSettings || {
      1: { itemsPerRow: 2, customCSS: '', name: 'Default Group', showGroupName: false }
    };

    const handleSave = async () => {
      try {
        await saveSectionData(sectionConfig, editData);
        toggleEditSection(sectionConfig.id);
      } catch (err) {
        console.error('Failed to save section:', err);
      }
    };

    const handleCancel = () => {
      // Clear editing data and exit edit mode
      setEditingData(prev => {
        const newData = { ...prev };
        delete newData[sectionConfig.id];
        return newData;
      });
      setEditingSections(prev => ({ ...prev, [sectionConfig.id]: false }));
      setCopiedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(sectionConfig.id);
        return newSet;
      });
      setToast && setToast(`🚫 Changes cancelled for ${sectionConfig.name}`);
      setTimeout(() => setToast && setToast(''), 3000);
    };

    return (
      <div key={sectionConfig.id} ref={sectionRefs.current[sectionConfig.id]}>
        <CollapsibleSection 
          title={sectionConfig.name} 
          sectionKey={sectionConfig.id} 
          isEmpty={!hasData}
          onSave={handleSave}
          onCancel={handleCancel}
          onAddData={() => handleAddDataToEmptySection(sectionConfig)}
          onCopyData={() => handleCopyDataFromLatest(sectionConfig)}
        >
          <div className="section-groups-container">
            {Object.entries(fieldGroups)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([groupId, fields]) => {
                const settings = groupSettings[groupId] || { itemsPerRow: 2, customCSS: '', name: `Group ${groupId}` };
                const groupClassName = `field-group-${sectionConfig.id}-${groupId}`;
                
                return (
                  <div key={groupId} className={`field-group ${groupClassName}`} style={{
                    gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                    ...(settings.customCSS && { 
                      background: 'unset',
                      padding: 'unset',
                      border: 'unset',
                      borderRadius: 'unset',
                      margin: 'unset'
                    })
                  }}>
                    {/* Add scoped custom CSS if provided */}
                    {settings.customCSS && (
                      <style>
                        {settings.customCSS
                          .replace(/\.field-group/g, `.${groupClassName}`)
                          .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                            const importantRules = rules.replace(
                              /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                              '$1$2 !important'
                            );
                            return `${selector}{${importantRules}}`;
                          })
                        }
                      </style>
                    )}
                    
                    {/* Show group title based on showGroupName setting */}
                    {settings.showGroupName && (
                      <div className="field-group-title" style={isPrint ? { fontSize: `${7 * fontScale}pt` } : { fontSize: `${14 * fontScale}px` }}>
                        {settings.name}
                      </div>
                    )}
                    
                    {/* Check if this is an Excel-style template that needs table structure */}
                    {settings.customCSS && settings.customCSS.includes('.field-grid') ? (
                      // Excel table structure with support for multiple columns
                      <div className="field-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                        gap: '0'
                      }}>
                        {fields.map(field => (
                          field.width === 'full' ? (
                            // Full width field spans all columns
                            <div key={field.id} className="field-row" style={{ 
                              gridColumn: '1 / -1',
                              display: 'grid',
                              gridTemplateColumns: '1fr 2fr',
                              border: '1px solid #d0d7de'
                            }}>
                              <div className="label" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>{field.name}</div>
                              <div className="value" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>
                                {isEditing && !isPrint ? (
                                  renderFieldInputToUse(
                                    field,
                                    sectionConfig.id,
                                    editData[field.dbColumn],
                                    handleFieldChangeWrapper
                                  )
                                ) : field.format === 'textarea' ? (
                                  <div className="field-textarea">
                                    <div dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                                  </div>
                                ) : (
                                  <div dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                                )}
                              </div>
                            </div>
                          ) : (
                            // Regular field - each field is a grid item that contains label+value
                            <div key={field.id} className="field-row">
                              <div className="label" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>{field.name}</div>
                              <div className="value" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>
                                {isEditing && !isPrint ? (
                                  renderFieldInputToUse(
                                    field,
                                    sectionConfig.id,
                                    editData[field.dbColumn],
                                    handleFieldChangeWrapper
                                  )
                                ) : field.format === 'textarea' ? (
                                  <div className="field-textarea">
                                    <div dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                                  </div>
                                ) : (
                                  <div dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                                )}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    ) : (
                      // Standard grid structure
                      <div className="field-group-content" style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${settings.itemsPerRow}, 1fr)`,
                        gap: '16px'
                      }}>
                        {fields.map(field => (
                          <div 
                            key={field.id} 
                            className={`label-value-item ${field.width === 'full' ? 'field-width-full' : ''}`}
                            style={field.width === 'full' ? { gridColumn: '1 / -1' } : {}}
                          >
                            <span className="label" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>{field.name}</span>
                            {isEditing && !isPrint ? (
                              renderFieldInputToUse(
                                field,
                                sectionConfig.id,
                                editData[field.dbColumn],
                                handleFieldChangeWrapper
                              )
                            ) : field.format === 'textarea' ? (
                              <div className="value field-textarea" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>
                                <div dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                              </div>
                            ) : (
                              <span className="value" style={isPrint ? { fontSize: `${4 * fontScale}pt` } : { fontSize: `${12 * fontScale}px` }}>
                                <span dangerouslySetInnerHTML={{ __html: formatFieldValue(data[field.dbColumn], field) }} />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </CollapsibleSection>
      </div>
    );
  };

  // Generate global styles from layout configuration
  const generateGlobalStyles = () => {
    if (!layoutConfig?.globalStyles) return {};
    
    const styles = layoutConfig.globalStyles;
    const baseFontSize = parseInt(styles.fontSize) || 14;
    
    return {
      fontFamily: styles.fontFamily || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      fontSize: isPrint ? `${5 * fontScale}pt` : `${baseFontSize * fontScale}px`,
      color: styles.textColor || '#1e293b',
      backgroundColor: styles.backgroundColor || '#ffffff',
      '--section-spacing': styles.sectionSpacing || '20px',
      '--section-padding': styles.sectionPadding || '16px',
      '--border-color': styles.borderColor || '#e5e7eb',
      '--border-radius': styles.borderRadius || '8px',
      '--font-scale': fontScale,
      '--section-header-background': styles.sectionHeaderBackgroundColor || '#f8fafc',
      '--section-header-font-size': styles.sectionHeaderFontSize || '16px',
      '--section-header-font-weight': styles.sectionHeaderFontWeight || '600',
      '--section-header-text-color': styles.sectionHeaderTextColor || '#1e293b'
    };
  };

  // Generate custom CSS from layout configuration - memoized for performance
  const generateCustomCSS = useMemo(() => {
    if (!layoutConfig) return '';
    
    let css = '';
    
    // Add section header styles from global configuration
    if (layoutConfig.globalStyles) {
      css += `
        .collapsible-section .section-header {
          background: var(--section-header-background) !important;
          font-size: var(--section-header-font-size) !important;
          font-weight: var(--section-header-font-weight) !important;
          color: var(--section-header-text-color) !important;
          padding: var(--section-padding) !important;
          border-bottom: 1px solid var(--border-color) !important;
        }
        
        .collapsible-section {
          border: 1px solid var(--border-color) !important;
          border-radius: var(--border-radius) !important;
          margin-bottom: var(--section-spacing) !important;
        }
        
        .section-content {
          padding: var(--section-padding) !important;
        }
      `;
    }
    
    // Add layout-specific custom CSS
    if (layoutConfig.customCSS) {
      css += layoutConfig.customCSS;
    }
    
    // Add section-specific CSS
    if (layoutConfig.sections) {
      layoutConfig.sections.forEach(section => {
        if (section.customCSS) {
          css += section.customCSS;
        }
        
        // Add table-specific custom CSS if available
        if (section.customTableCSS) {
          const tableClassName = `table-section-${section.id}`;
          css += `\n/* Custom CSS for ${section.name} table */\n`;
          css += section.customTableCSS
            .replace(/\btable\b/g, `.${tableClassName} table`)
            .replace(/\bth\b/g, `.${tableClassName} th`)
            .replace(/\btr\b/g, `.${tableClassName} tr`)
            .replace(/\btd\b/g, `.${tableClassName} td`)
            .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
              // Add !important to critical properties that might be overridden
              const importantRules = rules.replace(
                /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                '$1$2 !important'
              );
              return `${selector}{${importantRules}}`;
            }) + '\n';
        }
        
        // Add group-specific CSS
        if (section.groupSettings) {
          Object.entries(section.groupSettings).forEach(([groupId, settings]) => {
            if (settings.customCSS) {
              const groupClassName = `field-group-${section.id}-${groupId}`;
              css += `\n/* Custom CSS for ${section.name} - Group ${groupId} */\n`;
              css += settings.customCSS
                .replace(/\.field-group/g, `.${groupClassName}`)
                .replace(/([^{}]+){([^}]+)}/g, (match, selector, rules) => {
                  const importantRules = rules.replace(
                    /(background|padding|border|border-radius|margin|display|width|color|font-size|font-weight|line-height|vertical-align|border-collapse)(\s*:\s*[^;]+)/g,
                    '$1$2 !important'
                  );
                  return `${selector}{${importantRules}}`;
                }) + '\n';
            }
          });
        }
      });
    }
    
    return css;
  }, [layoutConfig]); // Only regenerate when layoutConfig changes

  const globalStyles = generateGlobalStyles();
  const customCSS = generateCustomCSS;

  // Helper function to get primary key column name for a table
  const getPrimaryKey = (tableName) => {
    const primaryKeys = {
      'Well': 'WellID',
      'DailyWellData': 'DailyID',
      'DailyActivity': 'ActivityID',
      'DailyPOB': 'AssignmentID',
      'DailyCasing': 'CasingActivityID',
      'DailyPerforation': 'PerforationID',
      'MudProperties': 'MudID',
      'Weather': 'WeatherID',
      'POB': 'POBID',
      'WellPlan': 'PlanID',
      'WellLog': 'LogID',
      'WellPerforation': 'PerforationID',
      'Casing': 'CasingID',
      'BHA': 'BHAID',
      'BitRecord': 'BitID',
      'BulkMaterial': 'MaterialID',
      'Attachment': 'AttachmentID',
      'POBAssignment': 'AssignmentID',
      'HoleSection': 'HoleID',
      'Completion': 'CompletionID'
    };
    return primaryKeys[tableName] || 'ID';
  };

  // Wrapper function for field changes
  const handleFieldChangeWrapper = (sectionId, fieldColumn, value) => {
    handleFieldChange(sectionId, fieldColumn, value);
  };

  // Helper function to format value for input editing
  const formatValueForInput = (value, field) => {
    if (!value) return '';
    
    // Determine the effective field type (using same logic as getInputType)
    const inputType = getInputType(field);
    
    // Handle different field types for editing
    if (inputType === 'date') {
      // Convert to YYYY-MM-DD format for date input
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } else if (inputType === 'datetime-local') {
      // Convert to YYYY-MM-DDTHH:mm format for datetime-local input
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
    } else if (inputType === 'time') {
      // Handle time format - preserve HH:mm or HH:mm:ss
      if (typeof value === 'string') {
        // Check if it's already in HH:mm or HH:mm:ss format
        if (value.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
          return value.substring(0, 5); // Return just HH:mm
        }
        
        // Check if it's a datetime string (ISO format or similar)
        if (value.includes('T') || value.includes(' ')) {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
          }
        }
        
        // Try to parse as time only (e.g., "17:00:00")
        const timeMatch = value.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (timeMatch) {
          const hours = String(parseInt(timeMatch[1])).padStart(2, '0');
          const minutes = String(parseInt(timeMatch[2])).padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      }
      
      // Last resort: try to parse as date and extract time
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      }
    }
    
    return value;
  };

  // Helper function to determine input type based on field
  const getInputType = (field) => {
    if (field.inputType) return field.inputType;
    
    // Check field type first
    switch (field.type) {
      case 'date':
        return 'date';
      case 'datetime':
      case 'datetime-local':
        return 'datetime-local';
      case 'time':
        return 'time';
      case 'number':
        return 'number';
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'url':
        return 'url';
    }
    
    // If no explicit type, try to infer from field name or dbColumn
    const fieldName = (field.name || field.dbColumn || '').toLowerCase();
    
    // Time field patterns
    if (fieldName.includes('time') && !fieldName.includes('datetime')) {
      return 'time';
    }
    
    // Date field patterns
    if (fieldName.includes('date') && !fieldName.includes('time')) {
      return 'date';
    }
    
    // DateTime field patterns
    if (fieldName.includes('datetime') || (fieldName.includes('date') && fieldName.includes('time'))) {
      return 'datetime-local';
    }
    
    // Number field patterns
    if (fieldName.includes('depth') || fieldName.includes('rate') || fieldName.includes('pressure') || 
        fieldName.includes('temperature') || fieldName.includes('weight') || fieldName.includes('volume') ||
        fieldName.includes('cost') || fieldName.includes('amount') || fieldName.includes('quantity')) {
      return 'number';
    }
    
    return 'text';
  };

  // Create renderFieldInput function for ReportRenderer
  const renderFieldInputDefault = (field, sectionId, value, updateEditingDataFn) => {
    const handleChange = (e) => {
      updateEditingDataFn(sectionId, field.dbColumn, e.target.value);
    };

    if (field.format === 'textarea') {
      return (
        <textarea
          value={value || ''}
          onChange={handleChange}
          className="form-control"
          rows={field.rows || 3}
          placeholder={field.placeholder || ''}
        />
      );
    }

    const inputType = getInputType(field);
    const formattedValue = formatValueForInput(value, field);

    return (
      <input
        type={inputType}
        value={formattedValue}
        onChange={handleChange}
        className="form-control"
        placeholder={field.placeholder || ''}
        step={field.type === 'number' ? (field.step || 'any') : undefined}
        min={field.min}
        max={field.max}
      />
    );
  };

  // Use enhanced renderFieldInput if provided, otherwise use default
  const renderFieldInputToUse = renderFieldInput || renderFieldInputDefault;

  // Create renderTableCellInput function for ReportRenderer
  const renderTableCellInputDefault = (field, sectionId, value, rowIndex, updateTableCellDataFn) => {
    const handleChange = (e) => {
      updateTableCellDataFn(sectionId, field.dbColumn, rowIndex, e.target.value);
    };

    if (field.format === 'textarea') {
      return (
        <textarea
          value={value || ''}
          onChange={handleChange}
          className="form-control table-cell-input"
          rows={field.rows || 2}
          placeholder={field.placeholder || ''}
        />
      );
    }

    const inputType = getInputType(field);
    const formattedValue = formatValueForInput(value, field);

    return (
      <input
        type={inputType}
        value={formattedValue}
        onChange={handleChange}
        className="form-control table-cell-input"
        placeholder={field.placeholder || ''}
        step={field.type === 'number' ? (field.step || 'any') : undefined}
        min={field.min}
        max={field.max}
      />
    );
  };

  // Use enhanced renderTableCellInput if provided, otherwise use default
  const renderTableCellInputToUse = renderTableCellInput || renderTableCellInputDefault;

  // Create updateTableCellData function for ReportRenderer
  const updateTableCellData = (sectionId, fieldColumn, rowIndex, value) => {
    handleTableFieldChange(sectionId, fieldColumn, rowIndex, value);
  };

  // Copy data from latest report
  const handleCopyDataFromLatest = async (sectionConfig) => {
    if (!well.WellID || !sectionConfig.tableName) return;

    // Only copy data for sections with reportId keyColumn
    if (sectionConfig.keyColumn !== 'reportId') {
      setToast('⚠️ Copy data is only available for report-specific sections');
      setTimeout(() => setToast(''), 3000);
      return;
    }

    try {
      setIsCopyingData(true);

      // Get the current report date to find reports before this date
      const currentReportDate = reportData?.ReportDate || reportDate;
      const currentDateStr = currentReportDate.split('T')[0]; // Ensure YYYY-MM-DD format

      console.log(`📋 Copying data from latest report before ${currentDateStr} for section: ${sectionConfig.id}`);

      const response = await apiFetch(`/api/ddr/latest-data/${well.WellID}/${sectionConfig.tableName}?beforeDate=${currentDateStr}`);

      if (response.data && response.data.length > 0) {
        console.log(`✅ Found ${response.data.length} records to copy from ${formatDate(response.latestReportDate)}`);

        if (sectionConfig.presentationType === 'table') {
          // For table sections, copy all rows and remove ID fields
          const copiedRows = response.data.map(row => {
            const newRow = { ...row };
            // Remove ID fields so new records will be created
            delete newRow[getPrimaryKey(sectionConfig.tableName)];
            delete newRow.ReportID; // Will be set when saving
            return newRow;
          });

          setEditingData(prev => ({
            ...prev,
            [sectionConfig.id]: {
              ...prev[sectionConfig.id],
              tableRows: copiedRows
            }
          }));
        } else {
          // For label-value sections, copy the first record
          const copiedData = { ...response.data[0] };
          // Remove ID fields so new record will be created
          delete copiedData[getPrimaryKey(sectionConfig.tableName)];
          delete copiedData.ReportID; // Will be set when saving

          setEditingData(prev => ({
            ...prev,
            [sectionConfig.id]: copiedData
          }));
        }

        // Mark this section as copied (for yellow background)
        setCopiedSections(prev => new Set([...prev, sectionConfig.id]));

        // Enter edit mode for this section
        setEditingSections(prev => ({ ...prev, [sectionConfig.id]: true }));

        setToast(`📋 Copied data from ${formatDate(response.latestReportDate)} report`);
        setTimeout(() => setToast(''), 3000);

      } else {
        const message = response?.message || 'No previous data found to copy';
        setToast(`ℹ️ ${message}`);
        setTimeout(() => setToast(''), 3000);
      }

    } catch (err) {
      console.error('Failed to copy data from latest report:', err);
      setToast('❌ Failed to copy data from latest report');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setIsCopyingData(false);
    }
  };

  return (
    <>
      <div className={`${isPrint ? 'print-container' : 'full-report-container'}`} style={globalStyles}>
        {customCSS && (
          <style dangerouslySetInnerHTML={{ __html: customCSS }} />
        )}
        
        <div className={`${isPrint ? 'print-content' : 'report-content'}`}>
          {layoutConfig?.sections
            ?.filter(section => section.enabled)
            ?.sort((a, b) => a.order - b.order)
            ?.map(section => renderDynamicSection(section))
          }
        </div>
      </div>

      {/* DataForm Popup - only show if not printing */}
      {!isPrint && showDataForm && dataFormConfig && (
        <div className="data-form-modal-overlay">
          <div className="data-form-modal">
            <div className="data-form-modal-header">
              <h3>🔧 Edit Full Data - {dataFormConfig.sectionConfig?.name}</h3>
              <p>Table: {dataFormConfig.tableName}</p>
            </div>
            <div className="data-form-modal-content">
              <DataForm
                data={dataFormConfig.data}
                tableName={dataFormConfig.tableName}
                onSave={handleDataFormSave}
                onCancel={handleDataFormCancel}
                title={`Edit ${dataFormConfig.sectionConfig?.name || 'Data'}`}
                skipAutoSave={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportRenderer; 